import {
	computed, observable, makeObservable,
} from 'mobx';
import { Axios } from '../utilities/network';
import { isWeb } from '../utilities/platform';
import RootStore from './root-store';
import { GetLocalStorage, SetLocalStorage } from '../utilities/cryptography';
import { USER_INFO } from '../constants/storage-keys';
import { navigate } from 'gatsby';
import { CURRENT_PLATFORM } from '../constants/settings';
import { ClinicMemberStatus, ClinicStatus, Gender, PAGE_SIZE } from '../constants/options';
import { ProfileTemplate } from '../utilities/profile';
import { GetApiLang, GetLangKey, GetLangLabel, GetUserProfile, IsClinicOwner, Language } from '../utilities/general';
import { IsZero } from '../utilities/field-validation';
import { get } from 'http';


export default class PractitionerStore {

	loading: boolean = false;

	loadingInfo: boolean = false;

	loadingArticles: boolean = false;

	updating: boolean = false;

	removing: boolean = false;

	loadingTimeslots: boolean = false;

	uploading: boolean = false;

	loadingSignature: boolean = false;

	creating: boolean = false;

	pendingCount: number = 0;

	isPendingCountChanged: boolean = false;

	loadingModality: boolean = false;

	// Not observable — a plain in-flight-request cache, not UI state. See
	// getRelatedClinics: several independent, unrelated call sites (the global
	// Layout's pending-count poller, /clinic's own initClinics, onboarding's
	// working-hours step) can all mount within the same tick and each fire
	// the default-shape call, producing duplicate network requests for
	// identical data. This coalesces those into one shared request.
	private relatedClinicsInFlight: Promise<any> | null = null;

	rootStore: RootStore;

	constructor(rootStore: RootStore) {
		makeObservable(this, {
			loading: observable,
			loadingInfo: observable,
			loadingArticles: observable,
			updating: observable,
			removing: observable,
			uploading: observable,
			loadingTimeslots: observable,
			loadingSignature: observable,
			creating: observable,
			pendingCount: observable,
			isPendingCountChanged: observable,
			loadingModality: observable,
		});
		this.rootStore = rootStore;
	}

	createProfile = async (reqModel: any, lang: Language = GetApiLang()) => {
		this.loading = true;
		try {
			const res = await Axios.post(`/api/Practitioner?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loading = false;
		}
	};

	getProfile = async (id: number = GetUserProfile().id, lang: Language = GetApiLang(), isloading: boolean = true) => {
		if (isloading) {
			this.loading = true;
		}
		try {
			const res = await Axios.get(`/api/Practitioner/${id}?lang=${lang}&platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			if (isloading) {
				this.loading = false;
			}
		}
	};

	updateProfile = async (reqModel: any, lang: Language = GetApiLang()) => {
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Practitioner?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	createProfileSkillKey = async (reqModel: any, lang: Language = GetApiLang()) => {
		this.updating = true;
		try {
			const res = await Axios.post(`/api/Practitioner/Skill/Key?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	updateProfileSkillKey = async (reqModel: any, lang: Language = GetApiLang()) => {
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Practitioner/Skill/Key?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	updateProfileSkillKeys = async (reqModel: any, lang: Language = GetApiLang()) => {
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Practitioner/Skill/Kiss?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	removeProfileSkillKey = async (id: number) => {
		this.removing = true;
		try {
			const res = await Axios.delete(`/api/Practitioner/Skill/Key/${id}?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.removing = false;
		}
	};

	searchArticles = async (searchTerm: string, lang: Language = GetApiLang()) => {
		const userInfo = GetUserProfile();
		this.loadingArticles = true;
		try {
			const res = await Axios.post(`/api/Practitioner/Article/Search/0/0?lang=${lang}&platformId=${CURRENT_PLATFORM}`, { searchTerm, practitionerId: userInfo.id });
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loadingArticles = false;
		}
	};

	getArticle = async (id: number, lang: Language = GetApiLang()) => {
		this.loading = true;
		try {
			const res = await Axios.get(`/api/Practitioner/Article/${id}?lang=${lang}&platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loading = false;
		}
	};

	createArticle = async (reqModel: any, lang: Language = GetApiLang()) => {
		this.loading = true;
		try {
			const res = await Axios.post(`/api/Practitioner/Article?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loading = false;
		}
	};

	updateArticle = async (reqModel: any, lang: Language = GetApiLang()) => {
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Practitioner/Article?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};



	toggleArticlePublish = async (id: number, isPublish: boolean, lang: Language = GetApiLang()) => {
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Practitioner/Article/${id}/${isPublish ? "Show" : "Hide"}?lang=${lang}&platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	removeArticle = async (id: number) => {
		this.removing = true;
		try {
			const res = await Axios.delete(`/api/Practitioner/Article/${id}?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.removing = false;
		}
	};

	updateStrengths = async (reqModel: any, lang: Language = GetApiLang()) => {
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Practitioner/Strengths?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	updateSpecialities = async (reqModel: any, lang: Language = GetApiLang()) => {
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Practitioner/Specialises?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	updateReviewBackground = async (reqModel: any) => {
		const userInfo = GetUserProfile();
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Practitioner/ReviewBg?platformId=${CURRENT_PLATFORM}`, { ...reqModel, practitionerId: userInfo.id });
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	removeReviewBackground = async (id: number) => {
		this.removing = true;
		try {
			const res = await Axios.delete(`/api/Practitioner/ReviewBg/${id}?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.removing = false;
		}
	};

	updateProfileImage = async (reqModel: any, userInfo: any = GetUserProfile()) => {
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Practitioner/ProfileImage?platformId=${CURRENT_PLATFORM}`, { ...reqModel, practitionerId: userInfo.id });
			SetLocalStorage(USER_INFO, { ...userInfo, profileImageSavedName: res.data });
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	updatePatientReview = async (reqModel: any, lang: Language = GetApiLang()) => {
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Practitioner/Patient/Reviews?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	updateSlug = async (slug: string) => {
		const userInfo = GetUserProfile();
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Practitioner/Slug?platformId=${CURRENT_PLATFORM}`, { id: userInfo.id, slug });
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	searchCourses = async (searchTerm: string, lang: Language = GetApiLang()) => {
		const userInfo = GetUserProfile();
		this.loading = true;
		try {
			const res = await Axios.post(`/api/Practitioner/Course/Search/0/0?lang=${lang}&platformId=${CURRENT_PLATFORM}`, { searchTerm, practitionerId: userInfo.id });
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loading = false;
		}
	};

	updateCourse = async (reqModel: any, lang: Language = GetApiLang()) => {
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Practitioner/Course?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	getCourse = async (id: number, lang: Language = GetApiLang()) => {
		this.updating = true;
		try {
			const res = await Axios.get(`/api/Practitioner/Course/${id}?lang=${lang}&platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	updateWorkingTime = async (reqModel: any) => {
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Clinic/Practitioner/WorkingTime?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	togglePublishProfile = async (id: number, isPublish: boolean) => {
		this.loading = true;
		try {
			const res = await Axios.put(`/api/Practitioner/${id}/${isPublish ? "Publish" : "Unpublish"}?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loading = false;
		}
	};

	searchBooking = async (skip: number, take: number = PAGE_SIZE, reqModel: any) => {
		const userInfo = GetUserProfile();
		this.loading = true;
		try {
			const res = await Axios.post(`/api/Practitioner/Booking/Search/${skip}/${take}?platformId=${CURRENT_PLATFORM}`, { ...reqModel, clinicId: userInfo.currentClinicId });
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loading = false;
		}
	};

	getBooking = async (id: number) => {
		this.loadingInfo = true;
		try {
			const res = await Axios.get(`/api/Practitioner/Booking/${id}?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loadingInfo = false;
		}
	};

	confirmBooking = async (id: number) => {
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Practitioner/Booking/Confirm/${id}?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	deleteBooking = async (id: number) => {
		this.removing = true;
		try {
			const res = await Axios.delete(`/api/Practitioner/Booking/${id}?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.removing = false;
		}
	};

	getTimeslots = async (reqModel: any) => {
		const userInfo = GetUserProfile();
		this.loadingTimeslots = true;
		try {
			const res = await Axios.post(`/api/Practitioner/Booking/Timeslots?platformId=${CURRENT_PLATFORM}`, { ...reqModel, practitionerId: userInfo.id, clinicId: userInfo.currentClinicId, isBooked: false });
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loadingTimeslots = false;
		}
	};

	getGeneralTimeslots = async (reqModel: any) => {
		const userInfo = GetUserProfile();
		this.loadingTimeslots = true;
		try {
			const res = await Axios.post(`/api/Practitioner/Booking/Timeslots?platformId=${CURRENT_PLATFORM}`, { ...reqModel, practitionerId: userInfo.id, clinicId: userInfo.currentClinicId });
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loadingTimeslots = false;
		}
	};

	assignPatientToAppointment = async (appointmentId: number, patientId: number) => {
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Practitioner/Booking/Assign?id=${appointmentId}&patientId=${patientId}&platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	getRelatedClinics = async (reqModel: any = {}, isRequiredInfoUpdate: boolean = true, lang: Language = GetApiLang()) => {
		// Only coalesce the plain, no-args call shape — the (clinicId, false)
		// callers (appointment pages, etc.) want a specific, independent
		// request and must not be merged with this one.
		const isDefaultShape = Object.keys(reqModel).length === 0 && isRequiredInfoUpdate;
		if (isDefaultShape && this.relatedClinicsInFlight) return this.relatedClinicsInFlight;

		const request = this.fetchRelatedClinics(reqModel, isRequiredInfoUpdate, lang);
		if (isDefaultShape) {
			this.relatedClinicsInFlight = request;
			// Release on the next microtask, not when the request itself
			// resolves — React flushes same-commit effects (e.g. the global
			// Layout poller and a page's own mount effect) synchronously back
			// to back, so this still merges those. Holding the cache for the
			// full request lifetime instead would risk serving a stale
			// snapshot to a call that legitimately needs a fresh round-trip
			// (e.g. clinic/info.tsx re-fetching right after a transfer-and-quit
			// mutation) just because it happened to land while an earlier,
			// unrelated call was still in flight.
			Promise.resolve().then(() => { this.relatedClinicsInFlight = null; });
		}
		return request;
	};

	private fetchRelatedClinics = async (reqModel: any, isRequiredInfoUpdate: boolean, lang: Language) => {
		this.loading = true;
		try {
			const res = await Axios.post(`/api/Practitioner/Clinic/Search/0/0?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
			if (!reqModel.id && isRequiredInfoUpdate) {
				let info = GetLocalStorage(USER_INFO);
				if (!info) return;
				const previousCurrentClinicId = info.currentClinicId;
				let clinics = res.data.items;
				let approvedClinics = clinics.filter((r: any) => r.clinicStatus == ClinicStatus.APPROVED && (r.status == ClinicMemberStatus.APPROVED || r.status == ClinicMemberStatus.PENDING_QUIT_APPROVAL));
				if (info.availableClinics)
					info.availableClinics = approvedClinics.map(((a: any) => ({
						id: a.clinicId,
						name: a.clinicName,
						address: a.clinicAddress,
						logo: a.clinicLogoSavedName,
						ownerId: a.ownerId,
						allOwners: a.allOwners ?? [],
						isOwner: Boolean(a.isOwner) || IsClinicOwner(a, info.id),
					})));

				if (info.availableClinics.length > 0) {
					if (IsZero(info.currentClinicId)) info.currentClinicId = info.availableClinics[0].id;
					else if (!info.availableClinics.find((ac: any) => ac.id === info.currentClinicId)) info.currentClinicId = info.availableClinics[0].id;
				} else info.currentClinicId = 0;
				// if (!(info.currentClinicId && info.availableClinics.find((ac: any) => ac.id === info.currentClinicId))) {
				// 	info.currentClinicId == 0;
				// }
				SetLocalStorage(USER_INFO, info);
				// USER_INFO is plain localStorage, not MobX state — this write alone
				// won't make an already-mounted `observer` (e.g. shared-layout.tsx's
				// Layout) re-render, so a member who just went from no clinic to
				// having one wouldn't see the nav update until something else
				// happened to re-render it. Nudge it explicitly when this auto-
				// assignment actually changed currentClinicId.
				if (info.currentClinicId !== previousCurrentClinicId) this.rootStore.userStore.bumpUserInfoVersion();
			}
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loading = false;
		}
	};

	getPendingClinicInvitations = async () => {
		this.setPendingCount(0);
		this.loading = true;
		try {
			const res = await Axios.post(`/api/Practitioner/Clinic/Search/0/1?lang=${GetApiLang()}&platformId=${CURRENT_PLATFORM}`, { status: ClinicMemberStatus.PENDING_PRACT_APPROVAL });
			this.setPendingCount(res.data.totalCount);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loading = false;
		}
	};

	setPendingCount = (count: number) => {
		this.pendingCount = count < 0 ? 0 : count;
		this.isPendingCountChanged = true;
	};

	setPendingCountChanged = (isChanged: boolean) => {
		this.isPendingCountChanged = isChanged;
	};

	searchPractitioners = async (skip: number, take: number = PAGE_SIZE, reqModel: any = {}, lang: Language = GetApiLang()) => {
		this.loading = true;
		try {
			const res = await Axios.post(`/api/Practitioner/Search/${skip}/${take}?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loading = false;
		}
	};

	approveBooking = async (id: number) => {
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Practitioner/Booking/Approve/${id}?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	createBooking = async (reqModel: any, lang: Language = GetApiLang()) => {
		const userInfo = GetUserProfile();
		this.creating = true;
		try {
			const res = await Axios.post(`/api/Practitioner/Booking?lang=${lang}&platformId=${CURRENT_PLATFORM}`, { ...reqModel, practitionerId: userInfo.id, clinicId: userInfo.currentClinicId });
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.creating = false;
		}
	};

	rescheduleBooking = async (reqModel: any, lang: Language = GetApiLang()) => {
		const userInfo = GetUserProfile();
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Practitioner/Booking/Reschedule?lang=${lang}&platformId=${CURRENT_PLATFORM}`, { ...reqModel, practitionerId: userInfo.id, clinicId: userInfo.currentClinicId });
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	updateBooking = async (reqModel: any, lang: Language = GetApiLang()) => {
		const userInfo = GetUserProfile();
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Practitioner/Booking?lang=${lang}&platformId=${CURRENT_PLATFORM}`, { ...reqModel, practitionerId: userInfo.id, clinicId: userInfo.currentClinicId });
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	updateColleagues = async (reqModel: any, lang: Language = GetApiLang()) => {
		this.updating = true;
		try {
			let newInfo: any[] = [...reqModel];
			newInfo.map((colleague: any) => {
				if (lang === Language.ZH_CN) {
					colleague.firstNameCn = colleague.firstName;
					colleague.lastNameCn = colleague.lastName;
					colleague.descCn = colleague.desc;
				} else {
					colleague.firstNameEn = colleague.firstName;
					colleague.lastNameEn = colleague.lastName;
					colleague.descEn = colleague.desc;
				}
			});
			const res = await Axios.put(`/api/Practitioner/Colleague?lang=${lang}&platformId=${CURRENT_PLATFORM}`, newInfo);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	searchColleagues = async (skip: number, take: number = PAGE_SIZE, reqModel: any = {}, lang: Language = GetApiLang()) => {
		const userInfo = GetUserProfile();
		this.loading = true;
		try {
			const res = await Axios.post(`/api/Practitioner/Colleague/Search/${skip}/${take}?lang=${lang}&platformId=${CURRENT_PLATFORM}`, { ...reqModel, memberId: userInfo.id });
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loading = false;
		}
	};

	publishColleague = async () => {
		const userInfo = GetUserProfile();
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Practitioner/Colleague/${userInfo.id}/Publish?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	unpublishColleague = async () => {
		const userInfo = GetUserProfile();
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Practitioner/Colleague/${userInfo.id}/UnPublish?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	uploadColleagueAttachments = async (reqModel: any) => {
		this.uploading = true;
		try {
			const res = await Axios.post(`/api/Practitioner/Colleague/Attachment?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.uploading = false;
		}
	};

	deleteColleagueAttachments = async (id: number) => {
		this.removing = true;
		try {
			const res = await Axios.delete(`/api/Practitioner/Colleague/Attachment/${id}?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.removing = false;
		}
	};

	searchDailySharings = async (skip: number, take: number = PAGE_SIZE, reqModel: any = {}, lang: Language = GetApiLang()) => {
		const userInfo = GetUserProfile();
		this.loading = true;
		try {
			const res = await Axios.post(`/api/Practitioner/Posts/Search/${skip}/${take}?lang=${lang}&platformId=${CURRENT_PLATFORM}`, { ...reqModel, memberId: userInfo.id });
			return Promise.resolve(res.data);

		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loading = false;
		}
	};

	createDailySharing = async (reqModel: any, lang: Language = GetApiLang()) => {
		const userInfo = GetUserProfile();
		this.updating = true;
		try {
			const res = await Axios.post(`/api/Practitioner/Post?lang=${lang}&platformId=${CURRENT_PLATFORM}`, { ...reqModel, memberId: userInfo.id });
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	updateDailySharing = async (reqModel: any, lang: Language = GetApiLang()) => {
		const userInfo = GetUserProfile();
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Practitioner/PostInfo?lang=${lang}&platformId=${CURRENT_PLATFORM}`, { ...reqModel, memberId: userInfo.id });
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	removeDailySharing = async (id: number) => {
		this.removing = true;
		try {
			const res = await Axios.delete(`/api/Practitioner/Post/${id}?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.removing = false;
		}
	};

	uploadSharingAttachment = async (reqModel: any) => {
		const userInfo = GetUserProfile();
		this.uploading = true;
		try {
			const res = await Axios.post(`/api/Practitioner/Post/Image`, { ...reqModel, memberId: userInfo.id });
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.uploading = false;
		}
	};

	removeSharingAttachment = async (id: number) => {
		this.uploading = true;
		try {
			const res = await Axios.delete(`/api/Practitioner/Post/Image/${id}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.uploading = false;
		}
	};

	publishDailySharing = async () => {
		const userInfo = GetUserProfile();
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Practitioner/Post/${userInfo.id}/Publish?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	unpublishDailySharing = async () => {
		const userInfo = GetUserProfile();
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Practitioner/Post/${userInfo.id}/Unpublish?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	createVideo = async (reqModel: any, lang: Language = GetApiLang()) => {
		const userInfo = GetUserProfile();
		this.updating = true;
		try {
			const res = await Axios.post(`/api/Practitioner/Video?lang=${lang}&platformId=${CURRENT_PLATFORM}`, { ...reqModel, memberId: userInfo.id });
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	updateVideo = async (reqModel: any, lang: Language = GetApiLang()) => {
		const userInfo = GetUserProfile();
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Practitioner/Video?lang=${lang}&platformId=${CURRENT_PLATFORM}`, { ...reqModel, memberId: userInfo.id });
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	deleteVideo = async (id: number) => {
		this.removing = true;
		try {
			const res = await Axios.delete(`/api/Practitioner/Video/${id}?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.removing = false;
		}
	};

	uploadVideoCover = async (reqModel: any) => {
		this.uploading = true;
		try {
			const res = await Axios.post(`/api/Practitioner/Video/Cover?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.uploading = false;
		}
	};
	removeVideoCover = async (id: number) => {
		this.removing = true;
		try {
			const res = await Axios.delete(`/api/Practitioner/Video/Cover/${id}?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.removing = false;
		}
	};

	uploadSignature = async (reqModel: any) => {
		this.loadingSignature = true;
		try {
			const res = await Axios.post(`/api/Practitioner/Signature?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loadingSignature = false;
		}
	};

	removeSignature = async (id: number) => {
		this.removing = true;
		try {
			const res = await Axios.delete(`/api/Practitioner/Signature/${id}?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.removing = false;
		}
	};

	getSignature = async () => {
		this.loadingSignature = true;
		try {
			const res = await Axios.get(`/api/Practitioner/Signature?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loadingSignature = false;
		}
	};

	modalitySearch = async (reqModel: any) => {
		this.loading = true;
		this.loadingModality = true;
		try {
			const res = await Axios.post(`/api/Clinic/Practitioner/Modality/Search/0/0?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loading = false;
			this.loadingModality = false;
		}
	};

	getTimeSlots = async (reqModel: any, lang: Language = GetApiLang()) => {
		this.loading = true;
		try {
			const res = await Axios.post(`/api/Practitioner/Booking/Timeslots?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loading = false;
		}
	};

	searchBlockoutTime = async (clinicId: number, practitionerId: number) => {
		this.loading = true;
		try {
			const res = await Axios.post(`/api/Practitioner/BlockoutTime?platformId=${CURRENT_PLATFORM}`, { clinicId, practitionerId });
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loading = false;
		}
	};


	changePassword = async (reqModel: any) => {
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Practitioner/Member/UpdatePassword?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	updateCMBA = async (reqModel: any, lang: Language = GetApiLang()) => {
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Practitioner/CMBA?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	getDashboardByPractitioner = async (reqModel: any) => {
		this.loading = true;
		try {
			const res = await Axios.post(`/api/Practitioner/Dashboard`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loading = false;
		}
	};
}
